<script setup lang="ts">
import mapboxgl from 'mapbox-gl';
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import * as turf from '@turf/turf';
import { Route } from "@/models/Route";
import { getTripItinerary } from "@/api";
import { useI18n } from "vue-i18n";

mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_API_KEY;

const props = defineProps<{
  id: string;
}>();

const status = ref<'loading' | 'success' | 'error'>('loading');
const routes = ref<Route[]>([]);
const selectedRouteId = ref<string | null>(null);
const selectedRoute = computed(() => routes.value.find(r => r.uuid === selectedRouteId.value));

const mapRef = ref<any>(null);
const mapMarkers = ref<any>([]);
const markers = ref<any>([]);
let map = ref<mapboxgl.Map | null>(null);

const { t } = useI18n(); // Use the i18n instance

watch(routes, (r) => {
  if (r.length > 0) {
    selectedRouteId.value = r[0].uuid;
  }
});

watch(selectedRouteId, async () => {
  if (!!selectedRoute.value === false) {
    return;
  }

  for (const marker of mapMarkers.value) {
    marker.remove();
  }

  await nextTick();
  selectedRoute.value.coordinates.forEach((point, idx) => {
    const marker = new mapboxgl.Marker({ element: markers.value[idx] })
      .setLngLat(point.coordinates)
      .setPopup(
        new mapboxgl.Popup({ offset: 25, closeButton: false })
          .setHTML(`
                  <div class="w-58 h-32 bg-primary-green rounded-7xl px-4 py-5">
                    <div class=" responsive-body-medium font-semibold">${t('itinerary2.day')} ${idx + 1}</div>
                    <div class="pt-1">
                        <div>
                            <span class="font-semibold pr-2">${t('itinerary2.from')}</span>
                            <span>${point.from}</span>
                        </div>
                        <div>
                            <span class="font-semibold pr-2">${t('itinerary2.to')}</span>
                            <span>${point.to}</span>
                        </div>
                    </div>
                    <div class="pt-3 flex flex-row items-center gap-3">
                        <div class="flex flex-row items-center gap-1">
                          <img src="/assets/icons/svg/distance_16x16.svg" alt="${t('itinerary2.distance')}">
                          <span>${point.distance}</span>
                        </div>
                        <div class="flex flex-row items-center gap-1">
                          <img src="/assets/icons/svg/Clock black_16x16.svg" alt="${t('itinerary2.time')}">
                          <span>${point.time}</span>
                        </div>
                        <div class="flex flex-row items-center gap-1">
                          <img src="/assets/icons/svg/fuel_16x16.svg" alt="${t('itinerary2.fuel')}">
                          <span>${point.fuel}</span>
                        </div>
                    </div>
                  </div>
                `)
      )
      .addTo(map.value);
    mapMarkers.value.push(marker);

    map.value.on('load', () => {
      const line = turf.lineString(selectedRoute.value.coordinates.map(c => c.coordinates));
      map.value.addSource('route', {
        type: 'geojson',
        data: turf.bezierSpline(line)
      });
      map.value.addLayer({
        id: 'routteste',
        type: 'line',
        source: 'route',
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': '#1b84ff',
          'line-width': 2,
          'line-dasharray': [3, 6],
        }
      });

      map.value.setCenter(selectedRoute.value.center);
    });
  });
});

onMounted(() => {
  getTripItinerary(props.id)
    .then((res) => {
      routes.value = res.data;
      // console.log(routes.value);
      status.value = 'success';
    })
    .catch((err) => {
      console.error(err);
      status.value = 'error';
    });

  map.value = new mapboxgl.Map({
    container: mapRef.value,
    style: 'mapbox://styles/mapbox/navigation-day-v1',
    zoom: 11
  });
});

onUnmounted(() => {
  if (map.value !== null) {
    map.value.remove();
    map.value = null;
  }
});
</script>
 
<template>
  <div class="container flex flex-row items-center gap-12 desktop">
    <div class="flex flex-col gap-6 overflowSidebar">
      <div v-for="route in routes" :key="route.uuid"
        class="w-[484px] h-67 p-5 mr-8 bg-gradient-to-r from-white to-gradients-aliceBlue rounded-11xl flex flex-row gap-6">
        <div :style="{ backgroundImage: `url('${route.image}')` }" class="w-[210px] h-56 bg-cover rounded-11xl flex">
          <div
            class="mt-3 ml-3.5 responsive-body-xsmall bg-white w-13 h-6 rounded-11xl flex items-center justify-center">
            {{ route.duration }}
          </div>
        </div>
        <div>
          <div class="responsive-title-5 font-semibold h-14">{{ route.name }}</div>
          <div class="mt-4">
            <div class="flex flex-row items-center gap-1">
              <img src="@/assets/icons/svg/Luggage_16x16.svg" alt="Suitcase">
              <span>{{ route.distance }}</span>
            </div>
            <div class="flex flex-row items-center gap-1">
              <img src="@/assets/icons/svg/Clock black_16x16.svg" alt="Suitcase">
              <span>{{ route.duration }}</span>
            </div>
            <div class="flex flex-row items-center gap-1">
              <img src="@/assets/icons/svg/fuel_16x16.svg" alt="Suitcase">
              <span>{{ route.fuel }}</span>
            </div>
          </div>
          <div class="mt-15  responsive-body-small font-semibold flex flex-row items-center gap-2.5 cursor-pointer"
            @click="selectedRouteId = route.uuid">
            <img src="@/assets/icons/svg/Right_16x16.svg" alt="right arrow">
            <span>{{ t('itinerary2.viewDetails') }}</span>
          </div>
        </div>
      </div>
    </div>
    <div ref="mapRef" class="flex-grow h-[572px] rounded-11xl">
      <div v-for="(point, idx) in (selectedRoute?.coordinates ?? [])" :key="idx" ref="markers"
        class="w-10 h-10 bg-primary-blue border-2 border-white rounded-full flex items-center justify-center text-white responsive-title-6">
        {{ idx }}
      </div>
    </div>
  </div>
  <div class="flex flex-row items-center gap-12 container mobile">
    <div class="flex flex-col gap-6 w-100">
      <div v-for="route in routes" :key="route.uuid">
        <div class="h-67 p-5 bg-gradient-to-r from-white to-gradients-aliceBlue rounded-11xl flex flex-row gap-1">
          <div :style="{ backgroundImage: `url('${route.image}')` }"
            class="w-[140px] h-[170px] bg-cover rounded-11xl flex">
            <div
              class="mt-3 ml-3.5 responsive-body-xsmall bg-white w-13 h-6 rounded-11xl flex items-center justify-center">
              {{ route.duration }}
            </div>
          </div>
          <div>
            <div class="responsive-title-6 font-semibold h-14">{{ route.name }}</div>
            <div class="mt-3.5">
              <div class="flex flex-row items-center gap-1">
                <img src="@/assets/icons/svg/Luggage_16x16.svg" alt="Suitcase">
                <span class="text-xs">{{ route.distance }}</span>
              </div>
              <div class="flex flex-row items-center gap-1">
                <img src="@/assets/icons/svg/Clock black_16x16.svg" alt="Suitcase">
                <span class="text-xs">{{ route.duration }}</span>
              </div>
              <div class="flex flex-row items-center gap-1">
                <img src="@/assets/icons/svg/fuel_16x16.svg" alt="Suitcase">
                <span class="text-xs">{{ route.fuel }}</span>
              </div>
            </div>
            <div class="mt-15  responsive-body-small font-semibold flex flex-row items-center gap-2.5 cursor-pointer"
              @click="selectedRouteId = route.uuid">
              <img src="@/assets/icons/svg/Right_16x16.svg" alt="right arrow">
              <span class="text-xs">{{ t('itinerary2.viewDetails') }}</span>
            </div>
          </div>
        </div>
        <div v-if="selectedRouteId === route.uuid" ref="mapRef" class="flex-grow h-[572px] rounded-11xl">
          <div v-for="(point, idx) in (selectedRoute.coordinates ?? [])" :key="idx" ref="markers"
            class="w-[22px] h-[22px] bg-primary-blue border-2 border-white rounded-full flex items-center justify-center text-white text-xs">
            {{ idx }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.overflowSidebar {
  overflow-y: scroll;
  height: 572px;
}

:deep(.mapboxgl-popup-content) {
  @apply p-0 rounded-11xl;
}

@media (max-width: 786px) {
  .desktop {
    display: none;
  }
}
@media (min-width: 786px) {
  .mobile {
    display: none;
  }

}
</style>
